import { z } from 'zod';

import { COMPOUND_OPERATOR_OR, creators, OPERATOR_IN, print } from '@atlaskit/jql-ast';
import { JiraIssueAri } from '@atlassian/ari/jira/issue';
import { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type {
	ActionRendererKeyToPayload,
	ActionStatusProps,
	ActionValidationResult,
	ActionWith,
	SerializableValue,
} from '@atlassian/conversation-assistant-message-actions-api/types';
import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';

import type { UpdateWorkItemAllFieldTypesAction } from '../../common/types';

import type { mainUpdateWorkItemAllFieldTypesActionQuery$data } from './__generated__/mainUpdateWorkItemAllFieldTypesActionQuery.graphql';

export const ISSUE_ID_FIELD_KEY = 'id';
export const ISSUE_KEY_FIELD_KEY = 'key';

const serializableValueSchema: z.ZodType<SerializableValue> = z.lazy(() =>
	z.union([
		z.string(),
		z.number(),
		z.boolean(),
		z.null(),
		z.undefined(),
		z.array(serializableValueSchema),
		z.record(serializableValueSchema),
	]),
);

const payloadSchema = z.object({
	issueIdOrKey: z.string(),
	fields: z
		.record(z.string(), serializableValueSchema)
		.refine((obj) => Object.keys(obj).length > 0, { message: 'fields object cannot be empty' }),
	fieldReasoning: z.record(z.string(), z.string()).optional(),
});

const actionSchema = z.object({
	key: z.literal(ActionKeys.UpdateWorkItemAllFieldTypes),
	data: z.object({
		updates: z
			.array(payloadSchema)
			.refine((updates) => updates.length > 0, { message: 'updates array cannot be empty' }),
		sourceUrl: z.string().optional(),
	}),
});

/**
 * Creates a lookup key for a field key and issue ID or key.
 * @param fieldKey - The field key to create a lookup key for.
 * @param issueIdOrKey - The issue ID or key to create a lookup key for.
 * @returns A lookup key for a field key and issue ID or key.
 */
export const createLookupKey = (fieldKey: string, issueIdOrKey: string): string =>
	`${fieldKey}:${issueIdOrKey}`;

/**
 * Extracts the data from the first field of an action.
 * @param action - The action to extract the data from.
 * @returns The data from the action.
 * @example
 * ```ts
 * const action = {
 *   key: 'updateWorkItemFields',
 *   data: { fields: { summary: 'New summary' }, fieldReasoning: { summary: 'The summary was updated' }, sourceUrl: 'https://example.com' },
 * };
 * const data = getActionData(action);
 * console.log(data); // { fieldKey: 'summary', fieldValue: 'New summary', fieldReasoning: 'The summary was updated', sourceUrl: 'https://example.com' }
 */
export const getActionData = (
	action: UpdateWorkItemAllFieldTypesAction,
): {
	issueIdOrKey: string;
	fieldKey: string;
	fieldValue: SerializableValue;
	fieldReasoning: string | undefined;
	sourceUrl: string | undefined;
} => {
	const { fields, fieldReasoning, issueIdOrKey } = action.data.updates[0];
	const { sourceUrl } = action.data ?? {};
	const fieldKey = Object.keys(fields)?.[0] || '';
	const fieldValue = fieldKey ? fields[fieldKey] : '';

	const reasoning = fieldKey && fieldReasoning ? fieldReasoning[fieldKey] : '';
	return { issueIdOrKey, fieldKey, fieldValue, fieldReasoning: reasoning, sourceUrl };
};

/**
 * Determines whether a given string is a potentially valid Jira issue key as it must contain a hyphen between letters and numbers.
 *
 * @param issueKey - The string to evaluate.
 * @returns `true` if the string is a potentially valid Jira issue key; otherwise, `false`.
 */
export const isIssueKey = (issueKey: string): boolean =>
	issueKey.match(/^[A-Za-z0-9]+-\d+$/) !== null;

/**
 * Determines whether a given string is a potentially valid Jira issue ID as it must be an integer.
 *
 * @param issueId - The string to evaluate.
 * @returns `true` if the string is a potentially valid Jira issue ID; otherwise, `false`.
 */
export const isIssueId = (issueId: string): boolean => Number.isInteger(Number(issueId));

/**
 * Validates a Jira "Update Work Item Fields" action payload against the expected schema.
 *
 * @param action - The action object to validate.
 * @returns An object indicating whether the action is valid. If valid, returns the typed action.
 *          If invalid, returns an error describing the validation failure.
 */
export function validateJiraUpdateWorkAllFieldTypesAction(
	action: MessageAction,
): ActionValidationResult<
	ActionRendererKeyToPayload[typeof ActionKeys.UpdateWorkItemAllFieldTypes]
> {
	const parsed = actionSchema.safeParse(action);
	if (parsed.success) {
		return {
			isValid: true,
			action: action as ActionWith<
				ActionRendererKeyToPayload[typeof ActionKeys.UpdateWorkItemAllFieldTypes]
			>,
		};
	}

	return {
		isValid: false,
		error: new Error(`Invalid ${action.key || 'unkown'} action payload: ${parsed.error.message}`),
	};
}

const SUPPORTED_FIELD_KEYS = new Set<string>([
	'summary',
	'description',
	'assignee',
	'reporter',
	'priority',
	'status',
	'labels',
	'parent',
	'duedate',
	'comment',
	'issuelinks',
	'issuetype',
	'timespent',
]);

const OTHER_FIELD_TYPE = 'other';
const CUSTOM_FIELD_TYPE = 'customfield';
const CUSTOM_FIELD_KEY_PATTERN = /^customfield_\d+$/;

export const toReportableFieldType = (fieldKey: string): string => {
	if (SUPPORTED_FIELD_KEYS.has(fieldKey)) {
		return fieldKey;
	}
	if (CUSTOM_FIELD_KEY_PATTERN.test(fieldKey)) {
		return CUSTOM_FIELD_TYPE;
	}
	return OTHER_FIELD_TYPE;
};

/**
 * Groups actions by their Jira issue ARI.
 *
 * Each action is assigned to an issue using its `issueIdOrKey`, resolved to an ARI via `cloudId` and optional
 * issue search `edges`. Returns an object whose `groupedActions` Map has issue ARIs as keys and arrays of
 * actions for that issue as values.
 *
 * @param validActions - Actions to group (each with work item field update data).
 * @param cloudId - Cloud/site ID used to build the Jira issue ARI.
 * @param edges - Optional issue search edges used to resolve issue keys to ARIs.
 * @returns groupedActions - a Map from issue ARI to array of actions for that issue.
 */
export const groupActionsByIssue = (
	validActions: UpdateWorkItemAllFieldTypesAction[],
	cloudId: string,
	edges: NonNullable<
		NonNullable<mainUpdateWorkItemAllFieldTypesActionQuery$data['jira']>['issueSearch']
	>['edges'] = [],
): Map<string, UpdateWorkItemAllFieldTypesAction[]> => {
	const groupedActions = new Map<string, UpdateWorkItemAllFieldTypesAction[]>();

	for (const action of validActions) {
		// Actions with multiple fields are bulk actions and hence should not render, but should be marked as bulk actions
		const issueAri = actionToIssueAri(action, cloudId, edges);
		if (issueAri) {
			const existingActions = groupedActions.get(issueAri) || [];
			groupedActions.set(issueAri, [...existingActions, action]);
		}
	}

	return groupedActions;
};

/**
 * Builds a JQL (Jira Query Language) string for a set of actions that update work item fields.
 *
 * This function extracts unique issue IDs from the provided actions and constructs a JQL query
 * that matches all issues with those IDs using the "IN" operator. The resulting JQL string
 * can be used in relay queries or other Jira API requests.
 *
 * @param actions - An array of actions containing payloads for updating work item fields.
 * @returns A JQL query string that selects issues by their IDs.
 */
export const buildJqlForActions = (actions: UpdateWorkItemAllFieldTypesAction[]): string => {
	// Retrieve the issueIds or issueKeys to formulate the JQL in our relay query
	const issueIdsOrKeys = Array.from(
		new Set(actions.map((action) => getActionData(action).issueIdOrKey)),
	);
	const issueIds = issueIdsOrKeys.filter((issueId) => isIssueId(issueId));
	const issueKeys = issueIdsOrKeys.filter((issueKey) => isIssueKey(issueKey));

	const jqlQuery = creators.query();

	if (issueIds.length > 0) {
		jqlQuery.appendClause(
			creators.terminalClause(
				creators.field(ISSUE_ID_FIELD_KEY),
				creators.operator(OPERATOR_IN),
				creators.listOperand(issueIds.map(creators.valueOperand)),
			),
			COMPOUND_OPERATOR_OR,
		);
	}

	if (issueKeys.length > 0) {
		jqlQuery.appendClause(
			creators.terminalClause(
				creators.field(ISSUE_KEY_FIELD_KEY),
				creators.operator(OPERATOR_IN),
				creators.listOperand(issueKeys.map(creators.valueOperand)),
			),
			COMPOUND_OPERATOR_OR,
		);
	}

	const jast = creators.jast(jqlQuery);

	return print(jast);
};

/**
 * Determines whether bulk action buttons should be disabled based on the provided actions.
 *
 * @param actions - An array of actions to evaluate.
 * @param getStatus - A function that returns the status of a given action.
 * @param getReadonly - A function that returns whether a given action is readonly.
 * @returns `true` if the bulk action buttons should be disabled (i.e., if there are no actions,
 *          or if any action is not idle or is readonly); otherwise, `false`.
 */
export function areBulkActionButtonsDisabled(
	actions: ActionWith<ActionRendererKeyToPayload[typeof ActionKeys.UpdateWorkItemAllFieldTypes]>[],
	getStatus: ActionStatusProps['getStatus'],
	getReadonly: ActionStatusProps['getReadonly'],
): boolean {
	if (actions.length === 0) {
		return true;
	}
	return actions.some((action) => {
		if (
			!(
				getStatus(action) === 'idle' ||
				getStatus(action) === 'finished' ||
				action.status === 'finished'
			)
		) {
			return true;
		}
		if (
			getReadonly(action) &&
			!(getStatus(action) === 'finished' || action.status === 'finished')
		) {
			return true;
		}
		return false;
	});
}

/**
 * Splits actions into single-field and bulk (multi-field) lists, and computes accepted single actions
 * and summary flags. Single actions for an issue that has no bulk action are also included in
 * bulkActions so they render in both contexts.
 *
 * @param actions - Valid non-cancelled actions to split and analyse.
 * @returns Object with singleActions, bulkActions.
 */
export const getSingleAndBulkActions = (
	actions: UpdateWorkItemAllFieldTypesAction[],
): {
	singleActions: UpdateWorkItemAllFieldTypesAction[];
	bulkActions: UpdateWorkItemAllFieldTypesAction[];
} => {
	const singleActions = actions.filter(
		(action) => Object.keys(action.data.updates?.[0]?.fields ?? {}).length === 1,
	);
	const bulkActions = actions.filter(
		(action) => Object.keys(action.data.updates?.[0]?.fields ?? {}).length > 1,
	);
	const bulkActionKeys = new Set(bulkActions.map((action) => getActionData(action).issueIdOrKey));
	// Single actions whose issue has no bulk action are also shown as "bulk" (single-issue group).
	singleActions
		.filter((action) => !bulkActionKeys.has(getActionData(action).issueIdOrKey))
		.forEach((action) => bulkActions.push(action));

	return {
		singleActions,
		bulkActions,
	};
};

/**
 * Converts an action to a Jira issue ARI.
 *
 * @param action - The action to convert to an ARI.
 * @param cloudId - The cloud/site ID used to construct the Jira issue ARI.
 * @param edges - The edges to search for the issue.
 * @returns A Jira issue ARI string or null if the action is not a valid issue ID or key.
 */
export const actionToIssueAri = (
	action: UpdateWorkItemAllFieldTypesAction,
	cloudId: string,
	edges: NonNullable<
		NonNullable<mainUpdateWorkItemAllFieldTypesActionQuery$data['jira']>['issueSearch']
	>['edges'] = [],
): string | null => {
	const issueIdOrKey = getActionData(action).issueIdOrKey;
	if (isIssueId(issueIdOrKey)) {
		return JiraIssueAri.create({
			siteId: cloudId,
			issueId: issueIdOrKey,
		}).toString();
		// As issueId may sometimes be hallucinated by the LLM to be an issueKey, we need to check if
		// we can retrieve the ARI from the issue itself.
	}
	if (isIssueKey(issueIdOrKey)) {
		const issue = edges?.find((edge) => edge?.node?.key === issueIdOrKey);
		// If issue can be found, use its ARI, otherwise return null.
		if (issue?.node?.id) {
			return issue?.node?.id;
		}
	}
	return null;
};

/**
 * Deduplicates a list of actions by their field key and issue ID.
 * @param actions - An array of actions to deduplicate.
 * @returns An array of deduplicated actions.
 *
 * @example
 * ```ts
 * const actions = [
 *   { key: 'updateWorkItemFields', data: { fieldKey: 'summary', issueId: '123' } },
 *   { key: 'updateWorkItemFields', data: { fieldKey: 'summary', issueId: '123' } },
 * ];
 * const deduplicatedActions = deduplicateActions(actions);
 * console.log(deduplicatedActions); // [{ key: 'updateWorkItemFields', data: { fieldKey: 'summary', issueId: '123' } }]
 */
export const deduplicateActions = (
	actions: UpdateWorkItemAllFieldTypesAction[],
): UpdateWorkItemAllFieldTypesAction[] => {
	const deduplicatedActions: UpdateWorkItemAllFieldTypesAction[] = [];
	const seenActions = new Set<string>();
	for (const action of actions) {
		const { issueIdOrKey } = getActionData(action);
		const fieldKeys = Object.keys(action.data.updates[0].fields);

		const uniqueKey = `${fieldKeys.join(',')}:${issueIdOrKey}`;
		if (!seenActions.has(uniqueKey)) {
			seenActions.add(uniqueKey);
			deduplicatedActions.push(action);
		}
	}
	return deduplicatedActions;
};

/*
	Checks if a given action is a "Update Work Item All Fields" action.
	@param action - The action to check.
	@returns `true` if the action is a "Update Work Item All Fields" action; otherwise, `false`.
	@example
	```ts
	const action = { key: 'updateWorkItemAllFields', data: { issueId: '123', fields: { summary: 'New summary' } } };
	const isUpdateWorkItemAllFieldsAction = isUpdateWorkItemAllFieldsAction(action);
	console.log(isUpdateWorkItemAllFieldsAction); // true
	```
*/
export const isUpdateWorkItemAllFieldsAction = (
	action: MessageAction,
): action is ActionWith<
	ActionRendererKeyToPayload[typeof ActionKeys.UpdateWorkItemAllFieldTypes]
> => action.key === ActionKeys.UpdateWorkItemAllFieldTypes;

/**
 * Cleans the fields for a bulk action by removing fields which are already accepted or cancelled.
 * @param bulkAction - The bulk action to clean.
 * @param actions - The actions to clean the fields for.
 * @param getEffectiveStatus - A function that returns the effective status of a given action.
 * @returns The cleaned bulk action.
 * @example
 * ```ts
 * const bulkAction = { key: 'updateWorkItemFields', data: { issueId: '123', fields: { summary: 'New summary', description: 'New description', assignee: 'New assignee' } } };

 * const actions = [
 *   { key: 'updateWorkItemFields', data: { issueId: '123', fields: { summary: 'New summary' }, status: 'accepted' } },
 *   { key: 'updateWorkItemFields', data: { issueId: '123', fields: { description: 'New description' }, status: 'cancelled' } },
 *   { key: 'updateWorkItemFields', data: { issueId: '123', fields: { assignee: 'New assignee' }, status: 'planned' } },
 * ];
 * const cleanedBulkAction = cleanFieldsForBulkAction(bulkAction, actions, 'getStatus');
 * console.log(cleanedBulkAction); // { key: 'updateWorkItemFields', data: { issueId: '123', fields: { assignee: 'New assignee' } } }
 */
export const cleanFieldsForBulkAction = (
	bulkAction: UpdateWorkItemAllFieldTypesAction,
	actions: UpdateWorkItemAllFieldTypesAction[],
	getEffectiveStatus: ActionStatusProps['getStatus'],
): {
	data: {
		updates: {
			issueIdOrKey: string;
			fields: {
				[k: string]: SerializableValue;
			};
		}[];
	};
} => {
	const { issueIdOrKey } = getActionData(bulkAction);

	const fieldsToRemove = new Set(
		actions
			.filter((action) => Object.keys(action.data.updates?.[0]?.fields ?? {}).length === 1)
			.filter((action) => getActionData(action).issueIdOrKey === issueIdOrKey)
			.filter((action) => !(action.status === 'planned' && getEffectiveStatus(action) === 'idle'))
			.map((action) => getActionData(action).fieldKey),
	);

	const updatedFields = Object.fromEntries(
		Object.entries(bulkAction.data.updates[0].fields).filter(([key]) => !fieldsToRemove.has(key)),
	);

	return {
		data: {
			updates: [
				{
					issueIdOrKey,
					fields: updatedFields,
				},
			],
		},
	};
};

/**
 * Returns the set of field IDs that the API returned for the given action's issue (fieldsByIdOrAlias).
 * Used to filter out invalid field keys before sending the action payload.
 *
 * @param action - The action to get valid field IDs for.
 * @param cloudId - The cloud/site ID.
 * @param edges - The issue search edges from the Relay query (each node has fieldsByIdOrAlias).
 * @returns Set of field IDs that are valid for this action's issue. Includes 'comment' as always valid,
 * and 'timespent' when the API returned 'timetracking'.
 */
export const getValidFieldIdsForAction = (
	action: UpdateWorkItemAllFieldTypesAction,
	cloudId: string,
	edges: NonNullable<
		NonNullable<mainUpdateWorkItemAllFieldTypesActionQuery$data['jira']>['issueSearch']
	>['edges'] = [],
): Set<string> => {
	const issueAri = actionToIssueAri(action, cloudId, edges);
	if (!issueAri) {
		return new Set();
	}
	const edge = edges?.find((e) => e?.node?.id === issueAri);
	const fieldIds = edge?.node?.fieldsByIdOrAlias?.map((f) => f?.fieldId) ?? [];
	const apiFieldIds = new Set<string>(
		fieldIds.filter((id): id is string => typeof id === 'string'),
	);
	// 'comment' is not in fieldsByIdOrAlias but is always valid for the payload.
	apiFieldIds.add('comment');
	// 'issuelinks' is not in fieldsByIdOrAlias but is always valid for the payload.
	apiFieldIds.add('issuelinks');
	// timespent is requested as timetracking in the query; if API returned timetracking, allow timespent in payload.
	if (apiFieldIds.has('timetracking')) {
		apiFieldIds.add('timespent');
	}
	return apiFieldIds;
};
